import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ListRow, Select, cn } from '@beatq/ui';

export function BusinessPageHeader({
  title,
  description,
  meta,
  actions,
}: {
  title?: string;
  description?: string;
  meta?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="biz-page-header flex flex-wrap items-start justify-between gap-field">
      <div className="min-w-0">
        {title ? <h1 className="m-0">{title}</h1> : null}
        {description ? (
          <p className={cn('biz-page-desc m-0', title && 'mt-xs')}>{description}</p>
        ) : null}
        {meta ? <p className="biz-meta m-0 mt-xs">{meta}</p> : null}
      </div>
      {actions ? <div className="biz-actions">{actions}</div> : null}
    </header>
  );
}

export type BizBadgeTone = 'success' | 'warning' | 'risk' | 'danger' | 'info' | 'neutral';

export function statusBadgeTone(status: string | null | undefined): BizBadgeTone {
  const s = (status ?? '').toUpperCase().replace(/\s+/g, '_');
  if (['ACTIVE', 'PAID', 'COMPLETE', 'COMPLETED', 'APPROVED', 'ON_TRACK'].includes(s)) {
    return 'success';
  }
  if (['AT_RISK'].includes(s)) return 'risk';
  if (['PENDING', 'ISSUED', 'PART_PAID', 'WARNING'].includes(s)) return 'warning';
  if (
    ['DECLINED', 'CANCELLED', 'OVERDUE', 'SUSPENDED', 'FAILED', 'REJECTED', 'CRITICAL'].includes(s)
  ) {
    return 'danger';
  }
  if (['DRAFT', 'INFO'].includes(s)) return 'info';
  if (['READY', 'UNREAD', 'AVAILABLE', 'NONE'].includes(s)) return 'neutral';
  return 'neutral';
}

/** @deprecated use statusBadgeTone — kept for existing tests. */
export function statusBadgeVariant(status: string | null | undefined): BizBadgeTone {
  return statusBadgeTone(status);
}

function toSentenceCase(raw: string): string {
  const spaced = raw.replace(/_/g, ' ').trim().toLowerCase();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function StatusBadge({
  status,
  className,
}: {
  status: string | null | undefined;
  className?: string;
}) {
  if (!status) return null;
  const tone = statusBadgeTone(status);
  const unread = status.toUpperCase() === 'UNREAD';
  return (
    <span className={cn('biz-badge', unread ? 'biz-badge-unread' : `biz-badge-${tone}`, className)}>
      {toSentenceCase(status)}
    </span>
  );
}

export function CodeChip({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: 'default' | 'role';
}) {
  return <span className={cn('biz-chip', variant === 'role' && 'biz-chip-role')}>{children}</span>;
}

export function MonoId({ children }: { children: ReactNode }) {
  return <span className="biz-mono-id">{children}</span>;
}

export function BizPanel({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('biz-panel', className)}>
      <header className="biz-panel-header">
        <h2 className="biz-panel-title">{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

export function BizStatStrip({ children }: { children: ReactNode }) {
  return <div className="biz-stat-strip">{children}</div>;
}

export function BizStatCard({
  label,
  value,
  valueClassName,
  hint,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
  hint?: string;
}) {
  return (
    <div className="biz-stat-card">
      <p className="biz-stat-label">{label}</p>
      <div className={cn('biz-stat-value', valueClassName)}>{value}</div>
      {hint ? <p className="biz-stat-hint">{hint}</p> : null}
    </div>
  );
}

export function BusinessList({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={cn('biz-list', className)}>{children}</ul>;
}

export function BusinessListItem({
  title,
  subtitle,
  status,
  trailing,
  href,
  actionLabel,
  indent,
  leading,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  status?: string | null;
  trailing?: ReactNode;
  href?: string;
  actionLabel?: string;
  indent?: boolean;
  leading?: ReactNode;
}) {
  return (
    <ListRow
      href={href}
      actionLabel={actionLabel}
      className={indent ? 'biz-row-indent' : undefined}
      trailing={
        <span className="biz-actions">
          {status ? <StatusBadge status={status} /> : null}
          {trailing}
        </span>
      }
    >
      {leading ? (
        <span className="flex items-start gap-inline">
          {leading}
          <span className="min-w-0">
            <span className="font-medium text-default">{title}</span>
            {subtitle ? <span className="biz-meta block">{subtitle}</span> : null}
          </span>
        </span>
      ) : (
        <>
          <span className="font-medium text-default">{title}</span>
          {subtitle ? <span className="biz-meta">{subtitle}</span> : null}
        </>
      )}
    </ListRow>
  );
}

export function BusinessFilters({ children }: { children: ReactNode }) {
  return <div className="biz-filters mb-field">{children}</div>;
}

export function BusinessFilterSelect({
  label,
  value,
  onChange,
  options,
  id,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  id?: string;
}) {
  const selectId = id ?? `filter-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <label className="biz-filter-field flex flex-col gap-xs" htmlFor={selectId}>
      {label}
      <Select
        id={selectId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    </label>
  );
}

export function KpiCard({
  label,
  value,
  hint,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  valueClassName?: string;
}) {
  return (
    <BizStatCard
      label={label}
      value={
        <>
          {value}
          {hint ? <p className="biz-meta m-0 mt-xs">{hint}</p> : null}
        </>
      }
      valueClassName={valueClassName}
    />
  );
}

export function BusinessProgress({
  value,
  label,
  thick,
  tone = 'primary',
  showLabel = true,
}: {
  value: number;
  label?: string;
  thick?: boolean;
  tone?: 'primary' | 'ok';
  /** When false, only show percentage (no left label). */
  showLabel?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const aria = label ? `${label} ${pct}%` : `${pct}%`;
  return (
    <div className="biz-progress">
      <div className="flex justify-between biz-meta">
        {showLabel && label ? <span>{label}</span> : <span />}
        <span>{pct}%</span>
      </div>
      <div
        className={cn(
          'biz-progress-track',
          thick && 'is-thick',
          tone === 'ok' ? 'is-ok-track' : 'is-risk-track',
        )}
        aria-label={aria}
      >
        <div
          className={cn('biz-progress-fill', tone === 'ok' && 'is-ok')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function PrimaryAction({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="biz-action biz-action-primary">
      {children}
    </Link>
  );
}

export function SecondaryAction({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="biz-action">
      {children}
    </Link>
  );
}

export function RowAction({
  to,
  children,
  onClick,
  variant = 'link',
}: {
  to: string;
  children: ReactNode;
  onClick?: () => void;
  variant?: 'link' | 'ghost' | 'warm';
}) {
  return (
    <Link
      to={to}
      className={cn(
        'biz-action',
        variant === 'link' && 'biz-action-row',
        variant === 'ghost' && 'biz-action-ghost',
        variant === 'warm' && 'biz-action-warm',
      )}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

export function GhostAction({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="biz-action biz-action-ghost">
      {children}
    </Link>
  );
}

export function TextAction({ to, children }: { to: string; children: ReactNode }) {
  return <SecondaryAction to={to}>{children}</SecondaryAction>;
}

export function initialsFromLabel(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return (label.slice(0, 2) || 'BA').toUpperCase();
}

export function businessDisplayName(input: {
  contactName?: string | null;
  legalName?: string | null;
  tradingName?: string | null;
  billingEmail?: string | null;
  email?: string | null;
}): string {
  return (
    input.contactName?.trim() ||
    input.tradingName?.trim() ||
    input.legalName?.trim() ||
    input.billingEmail?.trim() ||
    input.email?.trim() ||
    'Business account'
  );
}

/** NAV-1 account context — company name, never the signed-in contact. */
export function businessAccountContextName(input: {
  legalName?: string | null;
  tradingName?: string | null;
}): string {
  return input.tradingName?.trim() || input.legalName?.trim() || 'Business account';
}

export function SettingsSubnav({
  items,
  current,
}: {
  items: readonly { to: string; label: string; id: string }[];
  current: string;
}) {
  return (
    <nav className="biz-tabs" aria-label="Settings">
      {items.map((item) => (
        <Link
          key={item.id}
          to={item.to}
          className={cn('biz-tab', current === item.id && 'is-active')}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

export function BusinessPermissionAbsent({ message }: { message: string }) {
  return (
    <p role="status" className="b02-perm-absent m-0">
      {message}
    </p>
  );
}

export function BusinessStepUpNotice({ children }: { children?: ReactNode }) {
  return (
    <p role="alert" className="b02-step-up m-0">
      {children ?? 'Extra confirmation is required for this action.'}
    </p>
  );
}
